<script setup>
import { onMounted, ref } from 'vue'
// 학습종료 과정 열기(레거시 userStudyHistory.js 차시 목록): n차시 · 차시명 · 교육이수 시간 · 접속아이피 · 진도 · 수강하기.
// 학습기간 안이면 학습 화면, 복습기간이면 복습 화면(진도 미반영)으로 간다. 그 밖에는 수강하기를 막는다.
const props = defineProps({ enrollId: { type: [Number, String], required: true }, request: { type: Function, required: true }, messageOf: { type: Function, required: true } })
const data = ref(null), error = ref('')
const time = value => value ? new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(new Date(value)) : '-'
// 접속 주소는 앞 세 자리만 남긴 prefix(예: 106.245.254.0/24)라 마지막 자리를 *로 보인다.
const ip = value => !value ? '-' : value.endsWith('.0/24') ? value.replace(/\.0\/24$/, '.*') : value.replace(/::?\/48$/, ':*')
const target = lesson => data.value?.studyable ? `/my/learning/${props.enrollId}` : data.value?.reviewable ? `/my/review/${props.enrollId}/${lesson.itemId}` : null
async function load() {
  error.value = ''
  try { data.value = await props.request(`/api/user/enrollments/${props.enrollId}/lessons`) } catch (cause) { error.value = props.messageOf(cause) }
}
onMounted(load)
</script>
<template>
  <section class="study-lessons" aria-label="차시별 학습 이력">
    <p v-if="error" role="alert" class="lessons-error">{{ error }} <button type="button" @click="load">다시 시도</button></p>
    <p v-else-if="!data" role="status" class="lessons-loading">차시 학습 이력을 확인하고 있습니다.</p>
    <template v-else>
      <p v-if="data.reviewable" class="lessons-review">복습기간 : <strong>{{ data.reviewUntil }}</strong>까지 · 복습 학습은 진도율·수료에 반영되지 않습니다.</p>
      <ul class="lessons">
        <li v-for="lesson in data.lessons" :key="lesson.itemId">
          <span class="order">{{ lesson.order }}차시</span>
          <div class="info"><strong>{{ lesson.name }}</strong><span>교육이수 시간 : {{ time(lesson.lastStudiedAt) }}</span><span>접속아이피 : {{ ip(lesson.lastIp) }}</span></div>
          <span class="percent">{{ lesson.percent }}%</span>
          <NuxtLink v-if="target(lesson)" class="go" :to="target(lesson)" :aria-label="`${lesson.order}차시 ${data.studyable ? '수강하기' : '복습하기'}`">수강하기 <span aria-hidden="true">▸</span></NuxtLink>
          <span v-else class="go off" title="학습·복습기간이 끝났습니다.">수강하기 <span aria-hidden="true">▸</span></span>
        </li>
      </ul>
      <p v-if="!data.lessons.length" class="lessons-loading">차시 정보가 없습니다.</p>
    </template>
  </section>
</template>
<style scoped>
.study-lessons{min-width:0}
.lessons{margin:0;padding:0;list-style:none;border:1px solid #bfc3c4;background:#fff}
.lessons li{display:grid;grid-template-columns:90px minmax(0,1fr) 80px 110px;align-items:center;gap:8px;padding:10px 12px;border-top:1px solid #e3e6e8;color:#555;font-size:14px}
.lessons li:first-child{border-top:0}
.order{text-align:center;color:#333}
.info{display:flex;flex-direction:column;min-width:0;line-height:1.5}
.info strong{font-weight:400;color:#333}
.percent{text-align:center;color:#333}
.go{justify-self:end;display:inline-block;padding:3px 10px;border-radius:3px;background:#666;color:#fff;font-size:13px;line-height:20px;text-decoration:none;white-space:nowrap}
.go:hover,.go:focus-visible{background:#444}
.go.off{background:#bbb;cursor:default}
.lessons-loading,.lessons-error{margin:0;padding:12px;font-size:14px}
.lessons-error{color:#c73333}
.lessons-review{margin:0 0 8px;font-size:13px;color:#5b4a12}
@media(max-width:640px){.lessons li{grid-template-columns:60px minmax(0,1fr);}.percent,.go{grid-column:2;justify-self:start}}
</style>
